import { useEffect, useRef, useState } from 'react';
import { useCommendaAiActions } from './CommendaAiActionsContext';
import {
  QuestionAnsweredRecap,
  type QuestionRecapPair,
} from './QuestionAnsweredRecap';
import { type QuestionAsk, type QuestionRecord } from './question-block';

interface QuestionSegmentProps {
  toolCallId: string;
  /** The question as the assistant asked it, from the tool call. */
  ask: QuestionAsk;
  /** The stored record, present on a replayed message. Carries the answer once given. */
  record: QuestionRecord | null;
  /** Only the newest assistant turn can hold a live question. */
  isLastAssistantMessage: boolean;
  /** True once the user has replied after this message, whatever they replied. */
  hasFollowingReply: boolean;
}

function askedPairs(ask: QuestionAsk, answer: string): QuestionRecapPair[] {
  return ask.questions.map((question) => ({
    question: question.question,
    answer,
  }));
}

function recordPairs(record: QuestionRecord): QuestionRecapPair[] {
  return record.questions.map((question) => ({
    question: question.question,
    answer: question.skipped
      ? 'Skipped, left to the assistant'
      : (question.selectedAnswer ?? ''),
  }));
}

/** One question in the conversation: docked above the composer while it is open, and shown as the
 * question with its answer once it is not.
 *
 * A live question is handed to the conversation rather than drawn here, so a long option list keeps
 * its own question and actions in view instead of scrolling away with the transcript. */
export function QuestionSegment({
  toolCallId,
  ask,
  record,
  isLastAssistantMessage,
  hasFollowingReply,
}: QuestionSegmentProps) {
  const actions = useCommendaAiActions();
  const [isDismissed, setIsDismissed] = useState(false);

  // Presence is the answer: a bare Skip records an empty list, which is still an answer given.
  const answeredInSession = actions?.answeredInSession[toolCallId];
  const hasSessionAnswer = answeredInSession !== undefined;
  const isAnswered = record?.status === 'COMPLETED' || hasSessionAnswer;
  const isLive =
    actions !== null &&
    !isAnswered &&
    !isDismissed &&
    isLastAssistantMessage &&
    !hasFollowingReply;

  // `ask` is re-parsed from the message parts on every render, so it rides a ref: keeping it in
  // the dependency list would re-dock the card on each render that docking itself causes.
  const askRef = useRef(ask);
  askRef.current = ask;

  useEffect(() => {
    if (!isLive || !actions) return undefined;

    actions.setLiveCard({
      toolCallId,
      ask: askRef.current,
      onDismiss: () => setIsDismissed(true),
    });

    return () => actions.clearLiveCard(toolCallId);
  }, [isLive, actions, toolCallId]);

  // Docked above the composer; this position carries the answer once the question resolves.
  if (isLive) return null;

  if (record && record.status === 'COMPLETED')
    return <QuestionAnsweredRecap pairs={recordPairs(record)} />;

  // Answered a moment ago, before the record has been read back.
  if (hasSessionAnswer)
    return (
      <QuestionAnsweredRecap
        pairs={ask.questions.map((question, index) => ({
          question: question.question,
          answer:
            answeredInSession
              .find((answer) => answer.questionIndex === index)
              ?.chosen.join(', ') ?? 'Skipped, left to the assistant',
        }))}
      />
    );

  // Dismissed, or the conversation moved on. The question stands, unanswered.
  return (
    <QuestionAnsweredRecap
      pairs={askedPairs(ask, hasFollowingReply ? '' : 'Not answered')}
    />
  );
}
